/* eslint-disable func-names, no-undef */
import { parseAdUnits, parseVideoUnits } from './utils/parseAdUnits';
import { buckets } from './constants/buckets';
import { getBidderSettings } from './utils/bidderSettings';

const log = window.tv2.utils.setupLog('👨🏼‍⚖️ Header Bidding');

(function () {
  const FAIL_SAFE_TIMEOUT = 1000;
  const bidders = ['appnexus', 'rubicon', 'adform'];

  const pbjsConfig = {
    bidderTimeout: FAIL_SAFE_TIMEOUT,
    enableSendAllBids: true,
    s2sConfig: {
      endpoint: 'https://prebid.services.tv2.dk/openrtb2/auction',
      accountId: '1',
      enabled: false, // @TODO: prebid server is functional but deactivated for now
      adapter: 'prebidServer',
      bidders,
    },
    priceGranularity: { buckets },
    rubicon: {
      singleRequest: true,
    },
    currency: {
      adServerCurrency: 'DKK',
    },
    userSync: {
      filterSettings: {
        iframe: {
          bidders: '*',
          filter: 'include',
        },
      },
    },
    // Cache URL required by prebid video
    cache: {
      url: 'https://prebid.adnxs.com/pbc/v1/cache',
    },
  };

  // We will support both TCF 2.0 and TCF 1.1
  // eslint-disable-next-line no-underscore-dangle
  if (window.__tcfapi) {
    pbjsConfig.consentManagement = {
      gdpr: {
        cmpApi: 'iab',
        timeout: 8000,
        defaultGdprScope: true,
      },
    };
  } else {
    pbjsConfig.consentManagement = {
      cmpApi: 'iab',
      timeout: 2500,
      allowAuctionWithoutConsent: false,
    };
  }

  const bidderAliases = [
    ['appnexus', 'msupply'], // msupply sells over appnexus
    ['adform', '638'], // 638 (OMGz) sells over adform
    ['adform', '2606'], // 2606 (IUM) sells over adform
    ['adform', 'Concept'], // Concept sells over adform
    ['adform', '2572'], // Publicis media sells over adform
  ];

  const pbjsStatus = () => ({
    winners: pbjs.getAllPrebidWinningBids(),
    nobids: pbjs.getNoBids(),
  });

  function handleSync() {
    const highestBids = window.pbjs.getHighestCpmBids();
    const adUnitArray = window.pbjs.adUnits.map((unit) => unit.code);
    // http://prebid.org/dev-docs/publisher-api-reference.html#module_pbjs.setTargetingForGPTAsync
    window.pbjs.setTargetingForGPTAsync(adUnitArray, (googleAdSlot) => (adUnitCode) => {
      const googleAdPath = googleAdSlot.getAdUnitPath();
      // get eg. "Wallpaper" from /19784602/tv2dk-web/Nyheder/Nyheder_Underside/Nyheder_Underside_Topbanner_1--Wallpaper
      const matches = adUnitCode.match(/--([\w]+)$/);

      // Is the googleAdSlot the same as the adUnitCode (disregarding twin unit postfix)?
      const isTheSame = adUnitCode.indexOf(googleAdPath) !== -1;
      if (!isTheSame) {
        return false;
      }

      // Twin unit type or false
      const twinUnitType = (matches && matches[1]) ? matches[1].toLowerCase() : false;

      // Is this unit the one with the highest cpm?
      // Either there's no twin or no original or this is the twin and its cpm is
      // higher than the original
      let isHighestCPM = true;
      if (twinUnitType) {
        const bidForOriginal = highestBids.filter((bid) => bid.adUnitCode === googleAdPath)[0];
        const bidForTwin = highestBids.filter((bid) => bid.adUnitCode === adUnitCode)[0];
        isHighestCPM = (bidForTwin && !bidForOriginal) || (bidForTwin && bidForTwin.cpm >= bidForOriginal.cpm);
      }

      // if it is a twin and has the highest cpm
      if (twinUnitType && isHighestCPM) {
        googleAdSlot.setTargeting('hb_banner_type', twinUnitType);
      }

      // if it is the highest cpm
      return isHighestCPM;
    });
  }

  function prebidInit(adUnits, videoUnits) {
    // Set up bidder settings
    pbjs.bidderSettings = getBidderSettings(pbjs);

    // When auction initialised, send info
    pbjs.onEvent('auctionInit', (auction) => {
      log.info('Auction started', { auction });
    });

    // When auction ends
    pbjs.onEvent('auctionEnd', (auction) => {
      log.info('Auction ended', {
        requestedBids: auction.bidderRequests.length,
        receivedBets: auction.bidsReceived.length,
        auction,
      });
    });

    // If bidder times out, send warning
    pbjs.onEvent('bidTimeout', () => {
      log.warn('Bidder timeout', pbjsStatus());
    });

    // When ad fails to render, send error
    pbjs.onEvent('adRenderFailed', () => {
      log.error('Ad render failed', pbjsStatus());
    });

    pbjs.que.push(() => {
      bidderAliases.forEach((a) => pbjs.aliasBidder(a[0], a[1])); // Add bidder aliases
      pbjs.setConfig(pbjsConfig); // Configure prebid.js
      pbjs.addAdUnits(adUnits); // add Add units to pbjs
      pbjs.requestBids({ adUnits, bidsBackHandler: () => { pbjs.auctionEnded = true; } }); // request bids
      pbjs.addAdUnits(videoUnits);
    });
  }

  // When DOM is ready
  function domready() {
    // add auctionEnded and setTwinUnitTargeting
    window.pbjs.auctionEnded = false;
    window.pbjs.handleSync = handleSync;
    // Define ad units
    const adUnits = parseAdUnits(document.querySelectorAll('[data-ad]'));
    const videoUnits = parseVideoUnits();
    if (adUnits.length) {
      // Initiate the auction
      prebidInit(adUnits, videoUnits);
    }
  }

  try {
    // as in tv2ads.js, use hash to abort the prebid script during performance tests
    if (window.location.hash === '#b5efd781-75dc-44c3-bb7b-68e1263c410c') {
      log.info('Auctions aborted', {});
      return;
    }
    // Load SDK, then listen for DOM ready
    window.tv2.utils.loadScript('{{ASSET_URL}}/{{PREBID_SDK}}', () => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', domready);
      } else {
        domready();
      }
    });
  } catch (e) {
    log.error('header bidding script failed to execute', e);
  }
}());
